import { ArrowLeft, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { useApp } from "@/context/AppContext";
import Disclaimer from "@/components/journey/Disclaimer";

const field = "h-11 rounded-xl border-emerald-300/20 bg-black/20 text-emerald-50 placeholder:text-emerald-100/30";

export const isFormValid = (f) =>
  f.name.trim() && Number(f.age) >= 1 && Number(f.age) <= 120 && f.gender && f.address.trim() &&
  f.phone.replace(/\D/g, "").length >= 10 && f.occupation.trim() && f.service_id && f.consent;

function ServiceChoice() {
  const { settings, state, update, pick, t } = useApp();
  const set = (v) => update({ form: { ...state.form, service_id: v } });
  return (
    <RadioGroup value={state.form.service_id} onValueChange={set} className="grid gap-2 sm:grid-cols-2">
      {settings.services.map((s, i) => (
        <Label
          key={s.id}
          htmlFor={`svc-${s.id}`}
          data-testid={`service-option-${s.id}`}
          className={`flex cursor-pointer items-start gap-3 rounded-2xl border p-4 transition-colors ${
            state.form.service_id === s.id ? "border-emerald-300/70 bg-emerald-400/10" : "border-white/10 bg-black/15 hover:border-emerald-300/30"
          }`}
        >
          <RadioGroupItem id={`svc-${s.id}`} value={s.id} className="mt-1 border-emerald-300 text-emerald-300" />
          <span className="flex-1">
            <span className="block text-sm font-medium text-white">{i + 1}. {pick(s, "label")}</span>
            <span className="mt-1 block text-xs text-emerald-100/70">
              {pick(s, "duration") ? `${pick(s, "duration")} · ` : ""}
              <span className="text-amber-200">Rs {s.price}</span>
            </span>
          </span>
        </Label>
      ))}
    </RadioGroup>
  );
}

export default function StepDetails() {
  const { settings, state, update, t, pick } = useApp();
  const f = state.form;
  const set = (k) => (e) => update({ form: { ...f, [k]: e.target.value } });
  const tx = settings.texts;

  const next = () => {
    if (!isFormValid(f)) return toast.error(t("required"));
    update({ step: 3 });
  };

  return (
    <section className="glass space-y-6 rounded-[2rem] p-6 sm:p-10" data-testid="step-details">
      <Disclaimer testId="details-disclaimer" items={[pick(tx, "p2_disclaimer_1"), pick(tx, "p2_disclaimer_2")]} />
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-1.5 sm:col-span-2">
          <Label className="text-emerald-100/80">{t("name")}</Label>
          <Input data-testid="input-name" className={field} value={f.name} onChange={set("name")} maxLength={100} />
        </div>
        <div className="space-y-1.5">
          <Label className="text-emerald-100/80">{t("age")}</Label>
          <Input data-testid="input-age" type="number" min={1} max={120} className={field} value={f.age} onChange={set("age")} />
        </div>
        <div className="space-y-1.5">
          <Label className="text-emerald-100/80">{t("gender")}</Label>
          <div className="flex gap-2" data-testid="input-gender">
            {["male", "female", "other"].map((g) => (
              <button
                key={g}
                type="button"
                data-testid={`gender-${g}`}
                onClick={() => update({ form: { ...f, gender: g } })}
                className={`h-11 flex-1 rounded-xl border text-sm transition-colors ${f.gender === g ? "border-emerald-300 bg-emerald-400/15 text-white" : "border-emerald-300/20 bg-black/20 text-emerald-100/70"}`}
              >
                {t(g)}
              </button>
            ))}
          </div>
        </div>
        <div className="space-y-1.5 sm:col-span-2">
          <Label className="text-emerald-100/80">{t("address")}</Label>
          <Textarea data-testid="input-address" rows={2} className={`${field} h-auto`} value={f.address} onChange={set("address")} maxLength={500} />
        </div>
        <div className="space-y-1.5">
          <Label className="text-emerald-100/80">{t("phone")}</Label>
          <Input data-testid="input-phone" type="tel" placeholder="+91" className={field} value={f.phone} onChange={set("phone")} maxLength={20} />
        </div>
        <div className="space-y-1.5">
          <Label className="text-emerald-100/80">{t("occupation")}</Label>
          <Input data-testid="input-occupation" className={field} value={f.occupation} onChange={set("occupation")} maxLength={200} />
        </div>
      </div>
      <div className="space-y-3">
        <p className="font-display text-lg text-white">{t("helpType")}</p>
        <ServiceChoice />
      </div>
      <div className="rounded-2xl border border-white/10 bg-black/15 p-4">
        <p className="mb-2 text-xs uppercase tracking-[0.2em] text-amber-200">{t("consentTitle")}</p>
        <label className="flex cursor-pointer items-start gap-3 text-sm text-emerald-50/90">
          <Checkbox
            data-testid="consent-checkbox"
            checked={f.consent}
            onCheckedChange={(v) => update({ form: { ...f, consent: !!v } })}
            className="mt-0.5 border-emerald-300 data-[state=checked]:bg-emerald-400 data-[state=checked]:text-emerald-950"
          />
          <span>{pick(tx, "consent")}</span>
        </label>
      </div>
      <div className="flex items-center justify-between">
        <Button variant="ghost" data-testid="details-back-btn" onClick={() => update({ step: 1 })} className="rounded-full text-emerald-100 hover:bg-white/10">
          <ArrowLeft className="mr-2 h-4 w-4" /> {t("back")}
        </Button>
        <Button
          data-testid="details-next-btn"
          onClick={next}
          className={`h-12 rounded-full px-8 text-emerald-950 transition-all ${isFormValid(f) ? "bg-emerald-400 hover:bg-emerald-300" : "bg-emerald-400/40"}`}
        >
          {t("next")} <ArrowRight className="ml-2 h-4 w-4" />
        </Button>
      </div>
    </section>
  );
}
